import React, { memo, useMemo } from 'react';
import { CommonContainer } from '@/components/container';
import { DangerouslyHtml } from '@/components/item';
import { LoadingScreen } from '@/components/loading';
import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import { useGetFooterQuery, useIpInformation } from '@/hooks/CommonRequests';
import {
  ConnectWithUsText,
  FaxText,
  HotelReservationsText,
  PhoneText,
} from '@/lib/react-intl/TranslatedTexts';
import { IsDesktop } from '@/utils/mediaQuery';
import { getImageUrl } from '@/utils/modify-data/modifyData';
import {
  Address,
  ContactItem,
  ContactList,
  CopyRight,
  FooterBottom,
  FooterBottomWrapper,
  FooterBusinessNumber,
  FooterContainer,
  FooterHotelSection,
  FooterLink,
  FooterLogo,
  FooterLogoWrapper,
  FooterNav,
  FooterTop,
  FooterTopLeft,
  FooterTopRight,
  FooterMiddle,
  FooterMiddleRow,
  FooterMiddleLink,
  LinkGroup,
  MenuTitle,
  MenuWrapper,
  SectionHeader,
  SnsWrapper,
} from './FooterStyling';

// 중국 IP 접속 시 카지노 제외
const filterCasinoLinksForCN = (links = [], countryCode) =>
  links.filter((link) => !(countryCode === 'CN' && link.url.includes('casino')));

const Footer = () => {
  const { footerAddress, footerMenu, footerContact, footerSns, isLoading } = useGetFooterQuery();

  const lang = localStorage.getItem(LOCAL_LANGUAGE);
  const isKorean = lang === 'ko';

  const baseUrl = useMemo(() => {
    const domain = 'https://www.inspireresorts.com';
    if (lang !== null) return `${domain}/${lang}`;
  }, [lang]);

  const { ipInformation } = useIpInformation();

  const filteredFooterTopMenu = useMemo(
    () => filterCasinoLinksForCN(footerMenu?.[0]?.children, ipInformation?.countryCode),
    [footerMenu, ipInformation]
  );

  const filteredFooterMenu = useMemo(
    () => filterCasinoLinksForCN(footerMenu?.[1]?.children, ipInformation?.countryCode),
    [footerMenu, ipInformation]
  );

  const filteredFooterBottomMenu = useMemo(
    () => filterCasinoLinksForCN(footerMenu?.[2]?.children, ipInformation?.countryCode),
    [footerMenu, ipInformation]
  );

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <FooterContainer>
      <CommonContainer>
        <FooterTop>
          <FooterTopLeft>
            {footerAddress?.field_footerlogo && (
              <FooterLogoWrapper>
                <FooterLogo
                  data={getImageUrl(footerAddress?.field_footerlogo[0]?.src)}
                  alt={footerAddress.field_footerlogo[0]?.alt}
                />
              </FooterLogoWrapper>
            )}
          </FooterTopLeft>

          <FooterTopRight>
            <section>
              {footerMenu?.length > 0 && (
                <MenuWrapper key={footerMenu?.[0]?.entity_id}>
                  <MenuTitle
                    href={`${baseUrl}${footerMenu?.[0]?.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <DangerouslyHtml value={footerMenu?.[0]?.title} />
                  </MenuTitle>

                  <FooterNav>
                    {filteredFooterTopMenu.map((link) => {
                      return (
                        <FooterLink
                          href={`${baseUrl}${link.url}`}
                          key={link.entity_id}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DangerouslyHtml value={link.title} />
                        </FooterLink>
                      );
                    })}
                  </FooterNav>
                </MenuWrapper>
              )}
            </section>

            <section className="center">
              {footerMenu?.length > 0 && (
                <MenuWrapper key={footerMenu?.[1].entity_id}>
                  <FooterNav>
                    {filteredFooterMenu.map((link) => {
                      return (
                        <FooterLink
                          href={`${baseUrl}${link.url}`}
                          key={link.entity_id}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DangerouslyHtml value={link.title} />
                        </FooterLink>
                      );
                    })}
                  </FooterNav>
                </MenuWrapper>
              )}
            </section>

            <FooterHotelSection>
              <IsDesktop>
                <SectionHeader>
                  <HotelReservationsText />
                </SectionHeader>
              </IsDesktop>

              <Address>
                <div>
                  <DangerouslyHtml value={footerAddress?.field_address_line_1} />
                </div>

                <div>
                  <DangerouslyHtml value={footerAddress?.field_address_line_2} />
                </div>
              </Address>

              <ContactList>
                <ContactItem>
                  <span>
                    <PhoneText />
                  </span>

                  <a href={`tel:${footerContact?.field_phone_wd}`}>
                    {footerContact?.field_phone_wd}
                  </a>
                </ContactItem>

                <ContactItem>
                  <span>
                    <FaxText />
                  </span>

                  <a href={`tel:${footerContact?.field_phone_wd}`}>{footerContact?.field_fax_wd}</a>
                </ContactItem>

                {/* <ContactItem>
                  <span>
                    <RoomReservationText />
                  </span>

                  <a href={`tel:+${footerContact?.field_room_reservation_wd}`}>
                    {footerContact?.field_room_reservation_wd}
                  </a>
                </ContactItem> */}

                <a href={`mailto:${footerContact?.field_email_wd}`} style={{ marginTop: '2rem' }}>
                  {footerContact?.field_email_wd}
                </a>
              </ContactList>

              {isKorean && (
                <FooterBusinessNumber>
                  <p>(주)인스파이어인티그레이티드리조트</p>

                  <p>사업자등록번호 : 496-86-00049</p>

                  <p>통신판매신고번호 : 2023-인천중구-0804</p>

                  <p>대표이사 : 고규범</p>
                </FooterBusinessNumber>
              )}
            </FooterHotelSection>
          </FooterTopRight>
        </FooterTop>

        {footerMenu?.length > 2 && filteredFooterBottomMenu.length > 0 && (
          <FooterMiddle>
            <FooterMiddleRow>
              {filteredFooterBottomMenu.slice(0, 2).map((link) => {
                const isPrivacy = link.entity_id === '38' || link.entity_id === '309';

                return (
                  <FooterMiddleLink
                    to={`${baseUrl}${link.url}`}
                    key={link.entity_id}
                    bold={isPrivacy ? 'true' : 'false'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <DangerouslyHtml value={link.title} />
                  </FooterMiddleLink>
                );
              })}
            </FooterMiddleRow>

            {filteredFooterBottomMenu.length > 2 && (
              <FooterMiddleRow>
                {filteredFooterBottomMenu.slice(2).map((link) => {
                  const isPrivacy = link.entity_id === '38' || link.entity_id === '309';

                  return (
                    <FooterMiddleLink
                      to={`${baseUrl}${link.url}`}
                      key={link.entity_id}
                      bold={isPrivacy ? 'true' : 'false'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DangerouslyHtml value={link.title} />
                    </FooterMiddleLink>
                  );
                })}
              </FooterMiddleRow>
            )}
          </FooterMiddle>
        )}
      </CommonContainer>

      <FooterBottom>
        <CommonContainer>
          <FooterBottomWrapper>
            <section>
              <CopyRight>
                <DangerouslyHtml value={footerAddress?.field_copyright_footer} />
              </CopyRight>
            </section>

            <SnsWrapper>
              <IsDesktop>
                <b>
                  <ConnectWithUsText />
                </b>
              </IsDesktop>

              <LinkGroup>
                {footerSns?.field_facebook_connection !== '' && (
                  <li>
                    <a href={footerSns?.field_facebook_connection} target="_blank" rel="noreferrer">
                      {footerSns?.field_facebook_connection_logo && (
                        <object
                          data={`${process.env.REACT_APP_WEBSITE_DRUPAL_URL}${footerSns?.field_facebook_connection_logo[0]?.src}`}
                          aria-labelledby={footerSns.field_facebook_connection_logo[0]?.alt}
                        />
                      )}
                    </a>
                  </li>
                )}

                {footerSns?.field_instagram !== '' && (
                  <li>
                    <a href={footerSns?.field_instagram} target="_blank" rel="noreferrer">
                      {footerSns?.field_instagram_connection_logo && (
                        <object
                          type="image/svg+xml"
                          loading="lazy"
                          data={`${process.env.REACT_APP_WEBSITE_DRUPAL_URL}${footerSns?.field_instagram_connection_logo[0]?.src}`}
                          aria-labelledby={footerSns.field_instagram_connection_logo[0]?.alt}
                          alt={footerSns.field_instagram_connection_logo[0]?.alt}
                        />
                      )}
                    </a>
                  </li>
                )}

                {footerSns?.field_kakao_connection !== '' && (
                  <li>
                    <a href={footerSns?.field_kakao_connection} target="_blank" rel="noreferrer">
                      {footerSns?.field_kakao_connection_logo && (
                        <object
                          type="image/svg+xml"
                          data={`${process.env.REACT_APP_WEBSITE_DRUPAL_URL}${footerSns?.field_kakao_connection_logo[0]?.src}`}
                          aria-labelledby={footerSns.field_kakao_connection_logo[0]?.alt}
                        />
                      )}
                    </a>
                  </li>
                )}

                {footerSns?.field_wechat_connection !== '' && (
                  <li>
                    <a href={footerSns?.field_wechat_connection} target="_blank" rel="noreferrer">
                      {footerSns?.field_wechat_connection_logo && (
                        <object
                          type="image/svg+xml"
                          data={`${process.env.REACT_APP_WEBSITE_DRUPAL_URL}${footerSns?.field_wechat_connection_logo[0]?.src}`}
                          aria-labelledby={footerSns.field_wechat_connection_logo[0]?.alt}
                        />
                      )}
                    </a>
                  </li>
                )}

                {footerSns?.field_naver_connection !== '' && (
                  <li>
                    <a href={footerSns?.field_naver_connection} target="_blank" rel="noreferrer">
                      {footerSns?.field_naver_connection_logo && (
                        <object
                          type="image/svg+xml"
                          data={`${process.env.REACT_APP_WEBSITE_DRUPAL_URL}${footerSns?.field_naver_connection_logo[0]?.src}`}
                          aria-labelledby={footerSns.field_naver_connection_logo[0]?.alt}
                        />
                      )}
                    </a>
                  </li>
                )}
              </LinkGroup>
            </SnsWrapper>
          </FooterBottomWrapper>
        </CommonContainer>
      </FooterBottom>
    </FooterContainer>
  );
};

export default memo(Footer);
